import { forwardRef, type CSSProperties, type SVGAttributes } from 'react';

import { cn } from '../utils/cn';

import { PROMIEN_RDZENIA, SRODEK_SYGNETU, sciezkaKola, sciezkaWycinka } from './geometria';
import {
  MARGINES_ROZPADU,
  ROZPAD,
  SIATKA_ROZPADU,
  kawalkiRozpadu,
  type WariantRozpadu,
} from './rozpad';

/**
 * Sygnet rozsypany — ilustracja stanu awarii (strony błędów).
 *
 * Rdzeń to żywa sieć i Nexus, odłamki to organy. Znak rozpada się wzdłuż
 * WŁASNYCH, radialnych cięć, więc nadal jest znakiem Insolitum — tylko w
 * stanie, który opisuje strona (`rozpad.ts`): organy rozsypane, organ
 * zabłądzony, sieć poza zasięgiem, organy w serwisie, organ uśpiony.
 *
 * Organy dryfują powoli wzdłuż swoich dwusiecznych, każdy z inną fazą. Gdy
 * na ekranie błędu człowiek najedzie albo przejdzie klawiaturą na akcję
 * naprawczą, organy zbliżają się do sieci — system „chce się złożyć"
 * (`--leczenie`, sterowane przez `EkranBledu`/CSS). Pod
 * `prefers-reduced-motion` dryf staje, układ zostaje: to ilustracja stanu.
 *
 * Komponent jest serwerowy (bez hooków).
 */
export interface SygnetRozsypanyProps extends Omit<
  SVGAttributes<SVGSVGElement>,
  'width' | 'height' | 'viewBox' | 'children'
> {
  wariant?: WariantRozpadu;
  /** Bok w pikselach. Bez niego rozmiar ustala klasa CSS. */
  rozmiar?: number;
}

const RDZEN = sciezkaKola(SRODEK_SYGNETU, SRODEK_SYGNETU, PROMIEN_RDZENIA);

const liczba = (n: number): string => String(Math.round(n * 1000) / 1000);

export const SygnetRozsypany = forwardRef<SVGSVGElement, SygnetRozsypanyProps>(
  ({ wariant = 'blad', rozmiar, className, ...props }, ref) => {
    const kawalki = kawalkiRozpadu(wariant);
    return (
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        viewBox={`${-MARGINES_ROZPADU} ${-MARGINES_ROZPADU} ${SIATKA_ROZPADU} ${SIATKA_ROZPADU}`}
        width={rozmiar}
        height={rozmiar}
        fill="currentColor"
        aria-hidden
        focusable="false"
        className={cn('ids-rozpad', className)}
        data-wariant={wariant}
        {...props}
      >
        <path
          className="ids-rozpad__rdzen"
          data-przygaszony={ROZPAD[wariant].rdzenPrzygaszony || undefined}
          d={RDZEN}
        />
        {kawalki.map((k) => {
          const kat = (((k.od + k.do) / 2) * Math.PI) / 180;
          const ux = Math.cos(kat);
          const uy = Math.sin(kat);
          const u = k.ulozenie;
          return (
            <g
              key={k.id}
              className="ids-odlamek"
              data-odlamek={k.id}
              data-cien={u.cien || undefined}
              data-uspiony={u.uspiony || undefined}
              data-dryf={u.dryf !== 0 || undefined}
              style={
                {
                  '--dx': `${liczba(u.odsuniecie * ux)}px`,
                  '--dy': `${liczba(u.odsuniecie * uy)}px`,
                  '--obrot': `${liczba(u.obrot)}deg`,
                  '--ux': liczba(ux),
                  '--uy': liczba(uy),
                  '--amp': liczba(u.dryf),
                  '--ramp': `${liczba(u.dryfObrot)}deg`,
                  '--faza': liczba(k.faza),
                } as CSSProperties
              }
            >
              <g className="ids-odlamek__dryf">
                <path className="ids-odlamek__ksztalt" d={sciezkaWycinka(k)} />
              </g>
            </g>
          );
        })}
      </svg>
    );
  }
);

SygnetRozsypany.displayName = 'SygnetRozsypany';
